import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MagnifyingGlass, ArrowsLeftRight } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";

// Reusable "move to another account" picker. `endpoint` receives { customer_id }.
export const MoveAccountDialog = ({
  open,
  onClose,
  onDone,
  endpoint,
  title = "Move to another account",
  description = "Pick the account to move this to.",
  confirmLabel = "Move",
  excludeId = "",
}) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setQ("");
      setResults([]);
      setPicked(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      api
        .get("/customers", { params: { fields: "basic", q: q.trim(), limit: 20 } })
        .then(({ data }) => setResults((data || []).filter((c) => c.id !== excludeId)))
        .catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, open, excludeId]);

  const confirm = async () => {
    if (!picked) return toast.error("Pick an account first");
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, { customer_id: picked.id });
      toast.success(`Moved to ${data.customer_name || picked.name}`);
      onClose();
      onDone?.(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not move");
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="move-account-dialog" className="max-w-md border-zinc-800 bg-zinc-900">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base font-bold">
            <ArrowsLeftRight size={18} weight="bold" className="text-indigo-400" />
            {title}
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="relative">
            <MagnifyingGlass
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
            />
            <Input
              data-testid="move-account-search"
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search accounts by name or company…"
              className="h-11 border-zinc-800 bg-zinc-950 pl-9 text-sm"
            />
          </div>
          <div className="max-h-64 space-y-1 overflow-y-auto" data-testid="move-account-results">
            {results.length === 0 ? (
              <p className="px-1 py-4 text-center text-xs text-zinc-600">No accounts found</p>
            ) : (
              results.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  data-testid={`move-account-option-${c.id}`}
                  onClick={() => setPicked(c)}
                  className={`flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm transition-colors duration-150 ${
                    picked?.id === c.id
                      ? "border-indigo-500 bg-indigo-500/10 text-white"
                      : "border-zinc-800 bg-zinc-950 text-zinc-300 hover:border-zinc-600"
                  }`}
                >
                  <span className="truncate">
                    <span className="font-semibold">{c.name}</span>
                    {c.company ? <span className="text-zinc-500"> · {c.company}</span> : null}
                  </span>
                  {c.group ? (
                    <span className="ml-2 shrink-0 text-[10px] uppercase tracking-widest text-zinc-500">
                      {c.group}
                    </span>
                  ) : null}
                </button>
              ))
            )}
          </div>
        </div>
        <DialogFooter>
          <Button
            data-testid="move-account-confirm"
            disabled={busy || !picked}
            onClick={confirm}
            className="bg-indigo-500 hover:bg-indigo-400"
          >
            {busy ? "Moving…" : picked ? `${confirmLabel} to ${picked.name}` : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
